import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { MessagesSquare, RefreshCw } from "lucide-react"
import {
  fetchChatTranscripts,
  fetchChatTranscript,
  type ChatTranscriptSummary,
  type ChatTranscriptDetail,
} from "@/lib/api"
import { TranscriptTimeline, transcriptTimeline, type TranscriptView } from "@/components/transcript-timeline"
import { UserCombobox, type UserOption } from "@/components/user-combobox"
import { MemberMessagesPanel } from "@/components/member-messages"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn, nameOrPhone } from "@/lib/utils"

// The four starter chips the mobile chat overlay renders (PROMPT_SUGGESTIONS
// in offseason-rn ChatOverlay.tsx). A chip tap sends the label as an ordinary
// message with no distinguishing property, so exact-matching the opener is the
// only way to tell "tapped a chip" from "typed something" today. Anything that
// does not match is real, unprompted intent - which is the interesting bucket.
const STARTER_CHIPS = [
  "Book a class",
  "Generate my workouts",
  "Edit today's workout",
  "What can you help with?",
] as const

// Bucket key for "typed something else" - not a real chip string, so it can
// never collide with an exact opener match.
const TYPED_KEY = "__typed__"
const OPENER_CATEGORIES = [...STARTER_CHIPS, TYPED_KEY] as const

function classifyOpener(opener: string | null): string {
  const trimmed = (opener ?? "").trim()
  return (STARTER_CHIPS as readonly string[]).includes(trimmed) ? trimmed : TYPED_KEY
}

const DAY_RANGES = [7, 30, 90, 365]

function fmt(iso: string | null): string {
  if (!iso) return "-"
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
}

// The backend already tried their profile, the waitlist/allowlist row they
// signed up through, and their phone number — so a short id here means we hold
// nothing at all on them, not that this page forgot to look.
function displayName(s: { name: string | null; user_id: string | null }): string {
  // `nameOrPhone` because most iMessage members have never typed a name: the
  // backend labels them with the phone number it holds, and formatted is the
  // difference between a row you can act on and a row that looks broken.
  return nameOrPhone(s.name) || (s.user_id ? s.user_id.slice(0, 8) : "unknown")
}

// A session's only user-facing name: the one permanent per-member thread is
// always "Agent" (its title column is never set); every other thread is a
// member-nameable side conversation, falling back to "Untitled" until named.
function threadLabel(s: { type: string | null; title: string | null }): string {
  return s.type === "coach" ? "Agent" : (s.title || "Untitled")
}

interface UserGroup {
  userId: string
  name: string | null
  sessions: ChatTranscriptSummary[]
}

// What puts a thread at the top: the last time THEY wrote, never the last time
// we did. The agent now opens threads and follows up on its own, so ordering by
// `last_message_at` floated every unanswered outreach above the people actually
// replying — the exact conversations you open this page to read. Falls back to
// the thread stamp only for rows from before the backend carried this field.
function repliedAt(s: ChatTranscriptSummary): string {
  return s.last_user_message_at ?? s.opener_at ?? s.last_message_at ?? ""
}

// The list shows where each thread stands now: its newest message, ours or
// theirs. Opener triage lives in the chips above, which still read `opener`.
function LatestPreview({ s }: { s: ChatTranscriptSummary }) {
  const text = s.last_message ?? s.opener
  if (!text) return <em>no messages</em>
  return (
    <>
      {s.last_message_role === "assistant" && <span className="font-medium">Coach: </span>}
      {text}
      {(s.last_message ? s.last_message_truncated : s.opener_truncated) && "…"}
    </>
  )
}

function groupByUser(list: ChatTranscriptSummary[]): UserGroup[] {
  const byUser = new Map<string, ChatTranscriptSummary[]>()
  for (const s of list) {
    const key = s.user_id ?? s.id
    const existing = byUser.get(key)
    if (existing) existing.push(s)
    else byUser.set(key, [s])
  }
  const groups: UserGroup[] = []
  for (const [userId, sess] of byUser) {
    const sorted = [...sess].sort((a, b) => repliedAt(b).localeCompare(repliedAt(a)))
    groups.push({ userId, name: sorted[0].name, sessions: sorted })
  }
  groups.sort((a, b) =>
    repliedAt(b.sessions[0]).localeCompare(repliedAt(a.sessions[0])),
  )
  return groups
}

function TranscriptsTab() {
  const [days, setDays] = useState(30)
  const [searchParams] = useSearchParams()
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(() => {
    const id = searchParams.get("user")
    return id ? { id, first_name: searchParams.get("name") ?? id.slice(0, 8), last_name: "" } : null
  })
  const [sessions, setSessions] = useState<ChatTranscriptSummary[]>([])
  const [activeOpeners, setActiveOpeners] = useState<Set<string>>(new Set())
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [detail, setDetail] = useState<ChatTranscriptDetail | null>(null)
  const [view, setView] = useState<TranscriptView>("member")
  const [isLoading, setIsLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await fetchChatTranscripts({
        days,
        // ponytail: the endpoint's own ceiling, and it picks its 200 by last
        // message — so past 200 live threads the member-reply sort would only
        // be sorting the page it was handed. 122 threads exist today. Page it
        // when that number gets close.
        limit: 200,
        user_id: selectedUser?.id,
      })
      setSessions(data.sessions)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load transcripts")
      setSessions([])
    } finally {
      setIsLoading(false)
    }
  }, [days, selectedUser])

  useEffect(() => { void load() }, [load])

  // Open a transcript at its newest message. The thread still reads top-down
  // like any chat log — the alternative was rendering it newest-first, which
  // put every reply above the line it answered. `useLayoutEffect` so the jump
  // happens before paint and nobody watches it scroll.
  const readerRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const el = readerRef.current
    if (el && detail) el.scrollTop = el.scrollHeight
  }, [detail])

  const openSession = useCallback(async (id: string) => {
    setSelectedSessionId(id)
    setDetailLoading(true)
    setDetail(null)
    try {
      setDetail(await fetchChatTranscript(id))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load transcript")
    } finally {
      setDetailLoading(false)
    }
  }, [])

  function toggleOpenerFilter(key: string) {
    setActiveOpeners((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Counts always reflect the full window, not the active filter, so toggling
  // one category on never makes the others' totals disappear.
  const openerCounts = OPENER_CATEGORIES.map((key) => ({
    key,
    label: key === TYPED_KEY ? "Typed something else" : key,
    count: sessions.filter((s) => classifyOpener(s.opener) === key).length,
  }))

  const filteredSessions = activeOpeners.size === 0
    ? sessions
    : sessions.filter((s) => activeOpeners.has(classifyOpener(s.opener)))

  const userGroups = useMemo(() => groupByUser(filteredSessions), [filteredSessions])
  const selectedGroup = userGroups.find((g) => g.userId === selectedUserId) ?? null

  // Keep the selection coherent as the window/filter changes: drop a member who
  // no longer has a matching thread, and re-default to their newest thread if
  // the one on screen just got filtered out from under it.
  useEffect(() => {
    if (!selectedUserId) return
    const group = userGroups.find((g) => g.userId === selectedUserId)
    if (!group) {
      setSelectedUserId(null)
      setSelectedSessionId(null)
      setDetail(null)
      return
    }
    if (!selectedSessionId || !group.sessions.some((s) => s.id === selectedSessionId)) {
      void openSession(group.sessions[0].id)
    }
  }, [userGroups, selectedUserId, selectedSessionId, openSession])

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-card p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Window
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border px-2 py-1.5 text-sm text-foreground"
          >
            {DAY_RANGES.map((d) => (
              <option key={d} value={d}>last {d} days</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          User
          <UserCombobox value={selectedUser} onSelect={setSelectedUser} placeholder="All users" />
        </label>
        <button
          onClick={() => void load()}
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={cn("size-4", isLoading && "animate-spin")} />
          Refresh
        </button>
        <div className="ml-auto text-sm text-muted-foreground">
          {filteredSessions.length} conversation{filteredSessions.length === 1 ? "" : "s"} ·{" "}
          {userGroups.length} user{userGroups.length === 1 ? "" : "s"}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {sessions.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-semibold">How conversations start</div>
            {activeOpeners.size > 0 && (
              <button
                onClick={() => setActiveOpeners(new Set())}
                className="text-xs text-muted-foreground underline hover:text-foreground"
              >
                Clear filter
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {openerCounts.map(({ key, label, count }) => {
              const active = activeOpeners.has(key)
              const isTyped = key === TYPED_KEY
              return (
                <button
                  key={key}
                  onClick={() => toggleOpenerFilter(key)}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                    isTyped
                      ? active
                        ? "bg-emerald-700 text-white"
                        : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                      : active
                        ? "bg-slate-700 text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                  )}
                >
                  {label} · <strong>{count}</strong>
                </button>
              )
            })}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Chip taps arrive as plain messages, so these are exact string matches on the
            first thing the user sent (the list below previews the newest message). Click one or more to filter the lists below;
            clear them all to see everyone again.
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[460px_minmax(0,1fr)]">
        <div className="rounded-lg border bg-card divide-y max-h-[70vh] overflow-y-auto">
          {isLoading && userGroups.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          )}
          {!isLoading && userGroups.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground">
              No conversations match right now.
            </div>
          )}
          {userGroups.map((g) => {
            const latest = g.sessions[0]
            return (
              <button
                key={g.userId}
                onClick={() => setSelectedUserId(g.userId)}
                className={cn(
                  "w-full text-left p-3 hover:bg-accent transition-colors",
                  selectedUserId === g.userId && "bg-accent",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {displayName({ name: g.name, user_id: g.userId })}
                  </span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {fmt(latest.last_message_at)}
                  </span>
                </div>
                <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                  <LatestPreview s={latest} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {g.sessions.length} conversation{g.sessions.length === 1 ? "" : "s"}
                </div>
              </button>
            )
          })}
        </div>

        {/* Member name lives in the left pane only; this pane labels threads, not members. */}
        <div className="rounded-lg border bg-card flex max-h-[70vh] overflow-hidden">
          <div className="w-64 shrink-0 divide-y overflow-y-auto border-r">
            {!selectedGroup && (
              <div className="p-6 text-sm text-muted-foreground">
                Pick a user to see their chat threads.
              </div>
            )}
            {selectedGroup?.sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => void openSession(s.id)}
                className={cn(
                  "w-full text-left p-3 hover:bg-accent transition-colors",
                  selectedSessionId === s.id && "bg-accent",
                )}
              >
                <div className="text-sm font-medium">{threadLabel(s)}</div>
                <div className="mt-1 text-xs text-muted-foreground">{fmt(s.last_message_at)}</div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  <LatestPreview s={s} />
                </div>
              </button>
            ))}
          </div>

          <div ref={readerRef} className="min-w-0 flex-1 overflow-y-auto">
            {!selectedGroup && (
              <div className="p-6 text-sm text-muted-foreground">
                Select a user, then a chat, to read it here.
              </div>
            )}
            {selectedGroup && !detail && !detailLoading && (
              <div className="p-6 text-sm text-muted-foreground">
                Pick a conversation to read the full exchange.
              </div>
            )}
            {detailLoading && (
              <div className="p-6 text-sm text-muted-foreground">Loading transcript…</div>
            )}
            {detail && (
              <div className="p-4 space-y-4">
                <div className="flex items-baseline gap-2 border-b pb-3 text-xs text-muted-foreground">
                  <span>
                    {detail.messages.length} messages · started{" "}
                    {fmt(detail.session.created_at)}
                  </span>
                  <span className="font-mono text-[10px]">{detail.session.user_id}</span>
                  {detail.outbound_available && (
                    <label className="ml-auto flex items-center gap-1.5">
                      <select
                        value={view}
                        onChange={(e) => setView(e.target.value as TranscriptView)}
                        className="rounded border bg-background px-1.5 py-0.5 text-xs"
                      >
                        <option value="member">What the member saw</option>
                        <option value="audit">Every send attempt</option>
                      </select>
                    </label>
                  )}
                </div>
                <TranscriptTimeline
                  items={transcriptTimeline(detail, detail.outbound_available ? view : "member")}
                  memberName={displayName(detail.session)}
                  view={detail.outbound_available ? view : "member"}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function ChatTranscriptsPage() {
  const [tab, setTab] = useState("transcripts")
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <MessagesSquare className="size-6 text-muted-foreground" />
        <h1 className="text-2xl font-bold">Chat Transcripts</h1>
        <span className="text-sm text-muted-foreground">what members say, and what we text them</span>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="transcripts">Transcripts</TabsTrigger>
          <TabsTrigger value="send">Send message</TabsTrigger>
        </TabsList>
      </Tabs>
      <div hidden={tab !== "transcripts"}><TranscriptsTab /></div>
      <div hidden={tab !== "send"}><MemberMessagesPanel /></div>
    </div>
  )
}
